import { ApiResponse } from "@/types/api.types";
import baseApi from "./RootBaseApi";
import {
  CreateIconImagePayload,
  IIconImageData,
  IconLifecyclePayload,
  UpdateIconImagePayload,
} from "@/types/icon-images-management.types";

export const IconImagesManagementService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getIconImages: builder.query<
      ApiResponse<IIconImageData[]>,
      { lang: string }
    >({
      query: (params) => ({
        url: "images",
        method: "GET",
        params,
        headers: { "X-lang": params.lang },
      }),
      transformResponse: (response: ApiResponse<IIconImageData[]>) => response,
      providesTags: ["IconImages"],
      keepUnusedDataFor: 0,
    }),
    updateIconImage: builder.mutation<
      ApiResponse<IIconImageData>,
      UpdateIconImagePayload
    >({
      query: ({ id, file, name, lang }) => {
        const formData = new FormData();
        if (file) {
          formData.append("file", file);
        }
        if (name !== undefined) {
          formData.append("name", name);
        }
        return {
          url: `images/${id}`,
          method: "PUT",
          data: formData,
          headers: { "X-lang": lang },
        };
      },
      transformResponse: (response: ApiResponse<IIconImageData>) => response,
      invalidatesTags: ["IconImages"],
    }),
    updateIconLifecycle: builder.mutation<
      ApiResponse<IIconImageData>,
      IconLifecyclePayload
    >({
      query: ({ id, action, lang }) => ({
        url: `images/${id}/lifecycle`,
        method: "PATCH",
        data: { action },
        headers: { "X-lang": lang },
      }),
      transformResponse: (response: ApiResponse<IIconImageData>) => response,
      invalidatesTags: ["IconImages"],
    }),
    deleteIconImage: builder.mutation<void, { id: number; lang: string }>({
      query: ({ id, lang }) => ({
        url: `images/${id}`,
        method: "DELETE",
        headers: { "X-lang": lang },
      }),
      invalidatesTags: ["IconImages"],
    }),
    createIconImage: builder.mutation<
      ApiResponse<IIconImageData>,
      CreateIconImagePayload
    >({
      query: ({ name, file, lang }) => {
        const formData = new FormData();
        formData.append("name", name);
        formData.append("file", file);
        return {
          url: "images",
          method: "POST",
          data: formData,
          headers: { "X-lang": lang },
        };
      },
      transformResponse: (response: ApiResponse<IIconImageData>) => response,
      invalidatesTags: ["IconImages"],
    }),
  }),
});

export const {
  useGetIconImagesQuery,
  useUpdateIconImageMutation,
  useUpdateIconLifecycleMutation,
  useDeleteIconImageMutation,
  useCreateIconImageMutation,
} = IconImagesManagementService;
